import { LocalServiceWorker } from "./local-service-worker";

const clients = {}

/**
 * Makes a "request" against a URL *locally*. The URL will be loaded into
 * a Web Worker, which will then handle the request in another thread and
 * respond with a promise.
 * 
 * ```js
 * // Make the request.
 * const r = localFetch(
 * 	'./service-worker.js',
 * 	'doThing',
 * 	{ arg: 123 }
 * );
 * 
 * // Optionally watch for progress updates.
 * // (This is only meaningful if the worker provides updates.)
 * r.onprogress = pct => setProgress(pct);
 * 
 * // Wait for the result.
 * const data = await r.data;
 * ```
 * 
 * @param {string} url The URL of a JS script to load into a
 * Web Worker to handle the request
 * @param {string} method The method to ask the worker to execute.
 * @param {any[]} args The paremeters to send to the method.
 * @returns {LocalFetchResponse}
 */
export function localFetch(url, method, ...args) {
	if (!clients[url]) {
		clients[url] = new LocalServiceWorker(url);
	}
	return localFetchFromWorker(clients[url], method, ...args);
}

/**
 * Makes a "request" against an existing LocalServiceWorker.
 * 
 * ```js
 * // Make the request.
 * const r = localFetchFromWorker(
 * 	worker,
 * 	'doThing',
 * 	{ arg: 123 }
 * );
 * 
 * // Optionally watch for progress updates.
 * // (This is only meaningful if the worker provides updates.)
 * r.onprogress = pct => setProgress(pct);
 * 
 * // Wait for the result.
 * const data = await r.data;
 * ```
 * 
 * @param {LocalServiceWorker} worker And existing worker.
 * @param {string} method The method to ask the worker to execute.
 * @param {any[]} args The paremeters to send to the method.
 * @returns {LocalFetchResponse}
 */
export function localFetchFromWorker(worker, method, ...args) {
	const response = new LocalFetchResponse();
	worker.execute({
		method,
		args,
		oncomplete: result => response.resolve(result),
		onprogress: ({progress}) => response.onprogress(progress),
		onerror: error => response.reject(error),
	});
	return response;
}

/**
 * ```js
 * const r = fetchLocal();
 * r.onprogress = pct => setProgress(pct);
 * const data = await r.data;
 * ```
 */
class LocalFetchResponse {
	/**
	 * @type {Promise<any>}
	 */
	data;

	/**
	 * Provide a function to receive progress updates if/when the local
	 * service sends them.
	 * 
	 * ```js
	 * const r = fetchLocal();
	 * r.onprogress = progress => setProgress(progress);
	 * const data = await r.data;
	 * ```
	 * 
	 * @type {((progress) => void) | undefined | null}
	 */
	onprogress;

	/**
	 * @function
	 * @param {any} result
	 * 
	 * Called internally to report the result.
	 * 
	 * DO NOT CALL THIS OUTSIDE OF `local-service.js`.
	 */
	resolve;

	/**
	 * @function
	 * @param {any} error
	 * 
	 * Called internally to report failure.
	 * 
	 * DO NOT CALL THIS OUTSIDE OF `local-service.js`.
	 */
	reject;

	constructor() {
		this.data = new Promise((resolve, reject) => {
			this.resolve = resolve;
			this.reject = reject;
		})
	}
}

